import {
  Flex,
  Icon,
  Image,
  Link,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react"
import Constants from "Constants/shared.json"
import { Button, Heading } from "DesignSystem/components"
import { LinkExternalIcon } from "Icons/LinkExternalIcon"
import {
  DisplayModal,
  DisplayModalProps,
} from "Shared/components/DisplayModal/DisplayModal"
import { useLinkedinOAuth } from "UserCrowd/hooks/useLinkedinOAuth"
import React from "react"
import { useTrackEvent } from "~/api/generated/usabilityhub-components"
import ConnectedImage from "./linkedin-connected.png"
import SupportingImage from "./linkedin-promo.png"

type LinkedInVerificationModalProps = Pick<
  DisplayModalProps,
  "isOpen" | "onClose"
> & {
  source: string
}

export const LinkedInVerificationModal: React.FC<
  LinkedInVerificationModalProps
> = ({ isOpen, onClose, source }) => {
  const { mutate: trackEvent } = useTrackEvent()
  const { handleLinkedinOAuth, hasLinkedinOAuthCredentials } = useLinkedinOAuth(
    () => {
      trackEvent({
        body: {
          event: "panelist_linkedin_verification_completed",
          event_properties: { source },
        },
      })
    }
  )

  return (
    <DisplayModal isOpen={isOpen} onClose={onClose}>
      <DisplayModal.TwoPaneContent
        topHeight={["300px", "300px", "auto"]}
        content={
          <Flex
            h="full"
            w="full"
            bgColor="darkNeutral.opaque.800"
            borderTopRadius="24px"
            borderBottomRadius={[0, "24px"]}
            justifyContent="center"
            alignItems="center"
          >
            <Image
              src={
                hasLinkedinOAuthCredentials ? ConnectedImage : SupportingImage
              }
              w="full"
              maxW={["340px", "340px", "380px"]}
              objectFit="contain"
              alt="Researchers prefer LinkedIn verified testers"
            />
          </Flex>
        }
        controls={
          hasLinkedinOAuthCredentials ? (
            <Button variant="primary" onClick={onClose}>
              Got it!
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleLinkedinOAuth}>
                Sign in with LinkedIn
              </Button>
            </>
          )
        }
      >
        <Stack
          gap={4}
          minH={[0, "396px"]}
          justifyContent={["flex-start", "flex-start", "center"]}
        >
          {hasLinkedinOAuthCredentials ? (
            <>
              <Heading as="h2" textStyle="ds.heading.emphasized">
                Your account is connected! 🎉
              </Heading>
              <Text textStyle="ds.paragraph.emphasized">
                You are now LinkedIn verified. You{"\u2019"}re more likely to be
                selected for relevant, higher-paying studies.
              </Text>
              <Text textStyle="ds.paragraph.primary" color="ds.text.subtle">
                You can disconnect your LinkedIn account anytime from your
                profile page.
              </Text>
            </>
          ) : (
            <>
              <Heading as="h2" textStyle="ds.heading.emphasized">
                Verify your profile with LinkedIn
              </Heading>
              <Stack gap={3} textStyle="ds.paragraph.emphasized">
                <p>
                  Connect your LinkedIn account to verify your background and
                  unlock:
                </p>
                <UnorderedList>
                  <ListItem>More relevant research opportunities</ListItem>
                  <ListItem>Higher-paying studies for professionals</ListItem>
                  <ListItem>
                    Better matches between you and researchers
                  </ListItem>
                </UnorderedList>
              </Stack>
              <Text textStyle="ds.paragraph.primary" color="ds.text.subtle">
                By clicking Sign in with LinkedIn, you agree to let UserCrowd
                store your LinkedIn profile.
                <Link
                  textDecoration="none"
                  _hover={{ textDecoration: "none" }}
                  href={Constants.PANELIST_HELP_CENTER_MANAGING_PROFILE_URL}
                  rel="noopener noreferer"
                  isExternal
                >
                  {" "}
                  Learn more <Icon as={LinkExternalIcon} />
                </Link>
              </Text>
            </>
          )}
        </Stack>
      </DisplayModal.TwoPaneContent>
    </DisplayModal>
  )
}
